// The re-consent report (UC-GOVERNANCE-017 A4–A6; BR-GOVERNANCE-021, -027; PAT-74, R-15, Q-15) at /patients/re-consent: the live
// patients whose consent is not yet signed or is on replaced wording — or, when asked, withdrawn — in surname order, from
// `GET /re-consent-report`, with the number in each standing and the consent text in force. The standing and the home branch
// live in the address; each patient opens on their Consents tab, where the consent is recorded at the desk (that read is the
// patients module's and is logged there). Reading the report writes nothing. No prototype screen exists for it; it follows the
// turned-18 worklist's Operate pattern. A role without `patients.patient.read` sees the no-access panel.
import { RECONSENT_STANDINGS, type ReconsentEntry, type ReconsentReportPage, type ReconsentStanding } from "@visionx/contracts";
import { useQuery } from "@tanstack/react-query";
import { useState, type CSSProperties } from "react";
import { Link, useSearchParams } from "react-router";
import { consent as consentApi } from "../api/endpoints.consent";
import { isApiError } from "../api/problem";
import { formatDate } from "../lib/format";
import { Empty, ErrorAlert, Loading, Pager, Panel, Select } from "../ui/bits";
import { NoAccessPanel, PageHead } from "./common";
import { consentSignedHow } from "./PatientsConsents";
import { ConsentChip, PatientStatusChip, recordPath, useBranchNames } from "./PatientsShared";

const PAGE = 50;
const dash = <span className="faint">—</span>;
const STANDING_OPTION: Record<ReconsentStanding | "", string> = { "": "Not yet signed or outdated", None: "Not yet signed", Outdated: "Outdated", Withdrawn: "Withdrawn" };
/**
 * A filter shows its longest choice in full (UC-GOVERNANCE-017 A4.2): its box is at least that label's width — in `ch`, which
 * is no narrower than the average letter — plus the box's padding, border, gap and chevron, and never wider than the toolbar.
 */
const fitLongest = (labels: readonly string[]): CSSProperties => ({ minWidth: `min(calc(${Math.max(0, ...labels.map((l) => l.length))}ch + 56px), 100%)` });
const localDay = (d: string): string => formatDate(`${d}T00:00:00.000Z`);

function lastSigned(e: ReconsentEntry) {
  if (!e.lastSigned) return dash;
  const l = e.lastSigned;
  return <>v{l.textVersion} · {formatDate(l.signedAt)} · {consentSignedHow(l.channel, l.capturedOn)}{e.withdrawnAt && <div className="small faint">withdrawn {formatDate(e.withdrawnAt)}</div>}</>;
}

export function PatientsReconsent() {
  const { name: branchName, all: allBranches } = useBranchNames();
  const [sp, setSp] = useSearchParams();
  const statusParam = sp.get("status") ?? "";
  const status = (RECONSENT_STANDINGS as readonly string[]).includes(statusParam) ? (statusParam as ReconsentStanding) : undefined;
  const branchId = sp.get("branch") ?? undefined;
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const key = (c: string) => ["patients", "re-consent", status ?? "", branchId ?? "", c];
  // The counts walk every patient once: asked with the first page only, and kept while paging.
  const fetchPage = (c: string | undefined) => consentApi.reconsentReport({ status, branchId, count: c ? undefined : "true" }, { limit: PAGE, cursor: c });
  const first = useQuery({ queryKey: key(""), queryFn: () => fetchPage(undefined) });
  const later = useQuery({ queryKey: key(cursor ?? ""), queryFn: () => fetchPage(cursor), enabled: !!cursor });
  const q = cursor ? later : first;
  const page: ReconsentReportPage | null = q.data?.data ?? null;
  const counts = first.data?.data.counts;
  const text = first.data?.data.consentText ?? null;
  const rows = page?.items ?? [];
  const setParam = (k: string, v: string | null) => { setCursors([]); setSp((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true }); };
  const denied = q.isError && isApiError(q.error) && q.error.code === "PERMISSION_DENIED";
  const filtered = !!status || !!branchId;
  const offset = cursors.length * PAGE;
  // BR-GOVERNANCE-027: a request reads at most 10 000 patients; a short page with a cursor stopped there, not at the end.
  const stoppedEarly = !!page?.nextCursor && rows.length < PAGE;
  const standingOptions = (["", ...RECONSENT_STANDINGS] as const).map((s) => [s, `Consent · ${STANDING_OPTION[s]}`] as const);
  const branchOptions: (readonly [string, string])[] = [["", "Home branch · All branches"], ...allBranches.map((b) => [b.id, `Home branch · ${b.name}`] as const)];
  const standingFit = fitLongest(standingOptions.map(([, label]) => label));
  const branchFit = fitLongest(branchOptions.map(([, label]) => label));
  const sub = text
    ? `The patients to ask for consent at their next visit. Consent text v${text.version} has been in force since ${localDay(text.effectiveFrom)}; a patient who signed an earlier version is outdated.`
    : "The patients to ask for consent at their next visit. No consent text has been published yet.";

  return (
    <section className="screen" id="s-patients">
      <div className="content">
        <PageHead title="Re-consent" sub={sub} />
        <div className="stats">
          <div className="stat"><span className="v">{counts ? counts.None : "—"}</span><span className="l">Not yet signed</span><span className="d">no consent on record</span></div>
          <div className="stat"><span className="v">{counts ? counts.Outdated : "—"}</span><span className="l">Outdated</span><span className="d">{text ? `signed before v${text.version}` : "signed on replaced wording"}</span></div>
          <div className="stat"><span className="v">{counts ? counts.Withdrawn : "—"}</span><span className="l">Withdrawn</span><span className="d">care and records only, by their choice</span></div>
        </div>
        <div className="toolbar">
          <Select aria-label="Consent" boxStyle={standingFit} value={status ?? ""} onChange={(e) => setParam("status", e.target.value || null)}>
            {standingOptions.map(([s, label]) => <option key={s} value={s}>{label}</option>)}
          </Select>
          <Select aria-label="Home branch" boxStyle={branchFit} value={branchId ?? ""} onChange={(e) => setParam("branch", e.target.value || null)}>
            {branchOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </Select>
          <span className="help">{status === "Withdrawn" ? "Withdrawn patients chose it; they are not asked at check-in." : "Asked at check-in on Today and the calendar; nothing waits for it."}</span>
          <Link className="btn sm right" to="/patients">All patients</Link>
        </div>
        {q.isPending && q.fetchStatus !== "idle" && <Loading what="the re-consent report" />}
        {q.isError && (denied ? <NoAccessPanel page="the re-consent report" error={q.error} /> : <ErrorAlert error={q.error} />)}
        {page && (
          <Panel title={STANDING_OPTION[status ?? ""]} meta={rows.length === 0 ? (counts && !status ? `${counts.None + counts.Outdated} in all` : "") : `${offset + 1}–${offset + rows.length}${page.nextCursor ? " · more" : ""} · by surname`} tight
            foot={<>
              <span className="small faint">{stoppedEarly ? "This page stopped after reading 10 000 patients; Next continues from there." : "Opening a patient shows their Consents tab, where the consent is recorded at the desk."}</span>
              <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!page.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, page.nextCursor!])} />
            </>}>
            {rows.length === 0 ? (
              filtered || cursors.length > 0
                ? <Empty title="No patient matches these filters.">Choose another standing or branch.</Empty>
                : <Empty title="Every patient has a current consent.">Nobody is waiting to be asked; a new consent text version would list everyone who signed an earlier one.</Empty>
            ) : (
              <div className="tbl-scroll">
                <table>
                  <thead><tr><th>Patient</th><th>Home branch</th><th>Status</th><th>Consent</th><th>Last signed</th></tr></thead>
                  <tbody>
                    {rows.map((e) => (
                      <tr key={e.patientId}>
                        <td><Link className="nw" to={recordPath(e.patientId, "consents")}>{e.displayName}</Link><div className="sub mono">{e.accountCode}{e.isDependant ? " · dependant" : ""}</div></td>
                        <td>{branchName(e.homeBranchId) ?? dash}</td>
                        <td><PatientStatusChip status={e.patientStatus} /></td>
                        <td><ConsentChip status={e.standing} /></td>
                        <td className="nw">{lastSigned(e)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        )}
      </div>
    </section>
  );
}
